import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  ArrowLeft, Loader2, CheckCircle, XCircle, Clock, ShieldCheck,
  DollarSign, AlertCircle, Settings
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminWithdrawalsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('pending');
  const [withdrawals, setWithdrawals] = useState([]);
  const [kycs, setKycs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rate, setRate] = useState(null);
  const [newRate, setNewRate] = useState('');

  const adminEmails = ['adneryderisme@gmail.com'];
  const isAdmin = adminEmails.includes((user?.email || '').toLowerCase());

  const fetch = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [st, pendW, allW, pendK, r] = await Promise.all([
        axios.get(`${API}/admin/withdrawals/stats`, { headers }),
        axios.get(`${API}/admin/withdrawals?status=pending`, { headers }),
        axios.get(`${API}/admin/withdrawals`, { headers }),
        axios.get(`${API}/admin/withdrawals/kyc/pending`, { headers }),
        axios.get(`${API}/exchange-rate/current`, { headers }),
      ]);
      setStats(st.data);
      setWithdrawals(tab === 'pending' ? pendW.data : allW.data);
      setKycs(pendK.data);
      setRate(r.data);
    } catch (e) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin, tab]);

  useEffect(() => { fetch(); }, [fetch]);

  const action = async (path, payload, msg) => {
    try {
      await axios.post(`${API}${path}`, payload || {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(msg);
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    }
  };

  const approve = (id) => action(`/admin/withdrawals/${id}/approve`, { admin_notes: '' }, 'Approved');
  const reject = (id) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    action(`/admin/withdrawals/${id}/reject`, { reason }, 'Rejected & refunded');
  };
  const complete = (id) => {
    const ref = window.prompt('NatCash/MonCash transaction reference:');
    if (!ref) return;
    action(`/admin/withdrawals/${id}/complete`, { transaction_reference: ref }, 'Marked complete');
  };
  const kycApprove = (uid) => action(`/admin/withdrawals/kyc/${uid}/approve`, {}, 'KYC approved');
  const kycReject = (uid) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    action(`/admin/withdrawals/kyc/${uid}/reject`, { reason }, 'KYC rejected');
  };

  const updateRate = async () => {
    const v = parseFloat(newRate);
    if (!v || v < 50 || v > 500) {
      toast.error('Rate must be 50-500 HTG/USD');
      return;
    }
    try {
      await axios.post(`${API}/admin/withdrawals/exchange-rate`,
        { htg_per_usd: v },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Exchange rate updated');
      setNewRate('');
      fetch();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
            <p className="font-bold">Admin access required</p>
            <Button onClick={() => navigate('/')} className="mt-4">Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center gap-3 p-4 max-w-5xl mx-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Admin · Withdrawals</h1>
            <p className="text-xs text-muted-foreground">Review & process payouts</p>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-4 space-y-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-stats">
            <Card><CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-amber-500">{stats.pending}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-blue-500">{stats.approved}</p>
              <p className="text-xs text-muted-foreground">Approved</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-emerald-500">{stats.completed}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-red-500">{stats.rejected}</p>
              <p className="text-xs text-muted-foreground">Rejected</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-violet-500">${stats.outstanding_net_usd?.toFixed(0) || 0}</p>
              <p className="text-xs text-muted-foreground">Outstanding (net)</p>
            </CardContent></Card>
          </div>
        )}

        {/* Exchange rate */}
        <Card data-testid="exchange-rate-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Settings className="w-4 h-4" /> Exchange Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-2">
              Current: <span className="font-bold text-primary">1 USD = {rate?.htg_per_usd} HTG</span>
              {' '}<span className="text-xs">({rate?.source})</span>
            </p>
            <div className="flex gap-2">
              <Input
                type="number"
                step="0.01"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                placeholder="New HTG/USD rate"
                data-testid="rate-input"
              />
              <Button onClick={updateRate} data-testid="rate-update-btn">Update</Button>
            </div>
          </CardContent>
        </Card>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="pending" data-testid="tab-pending-w">
              Pending W. ({stats?.pending || 0})
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all-w">All Withdrawals</TabsTrigger>
            <TabsTrigger value="kyc" data-testid="tab-kyc">KYC Queue ({kycs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-2">
            {withdrawals.filter(w => w.status === 'pending').length === 0 ? (
              <Card><CardContent className="py-8 text-center text-muted-foreground">
                No pending withdrawals.
              </CardContent></Card>
            ) : withdrawals.filter(w => w.status === 'pending').map((w) => (
              <WithdrawalRow key={w.id} w={w} onApprove={() => approve(w.id)}
                              onReject={() => reject(w.id)} onComplete={() => complete(w.id)} />
            ))}
          </TabsContent>

          <TabsContent value="all" className="space-y-2">
            {withdrawals.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-muted-foreground">
                No withdrawals yet.
              </CardContent></Card>
            ) : withdrawals.map((w) => (
              <WithdrawalRow key={w.id} w={w} onApprove={() => approve(w.id)}
                              onReject={() => reject(w.id)} onComplete={() => complete(w.id)} />
            ))}
          </TabsContent>

          <TabsContent value="kyc" className="space-y-2">
            {kycs.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-muted-foreground">
                No pending KYC submissions.
              </CardContent></Card>
            ) : kycs.map((k) => (
              <KYCRow key={k.user_id} k={k} onApprove={() => kycApprove(k.user_id)}
                       onReject={() => kycReject(k.user_id)} />
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function WithdrawalRow({ w, onApprove, onReject, onComplete }) {
  const statusColor = {
    pending: 'text-amber-500 bg-amber-500/10',
    approved: 'text-blue-500 bg-blue-500/10',
    completed: 'text-emerald-500 bg-emerald-500/10',
    rejected: 'text-red-500 bg-red-500/10',
  }[w.status] || 'text-gray-500 bg-gray-500/10';

  return (
    <Card data-testid={`admin-w-${w.id}`}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold">@{w.username}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${statusColor}`}>
                {w.status.toUpperCase()}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary font-semibold uppercase">
                {w.method}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{w.email} · {w.method === 'paypal' ? (w.paypal_email || w.phone || '—') : w.phone}</p>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold">${w.amount_usd?.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">Net: ${w.net_usd?.toFixed(2)} ({w.net_htg?.toFixed(0)} HTG)</p>
            <p className="text-[10px] text-muted-foreground">{new Date(w.created_at).toLocaleString()}</p>
          </div>
        </div>
        {w.note && <p className="text-xs italic text-muted-foreground">"{w.note}"</p>}
        {w.rejection_reason && <p className="text-xs text-red-500">Reason: {w.rejection_reason}</p>}
        {w.transaction_reference && <p className="text-xs text-emerald-500">Ref: {w.transaction_reference}</p>}

        {w.status === 'pending' && (
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={onApprove} data-testid={`approve-${w.id}`} className="flex-1">
              <CheckCircle className="w-4 h-4 mr-1" /> Approve
            </Button>
            <Button size="sm" onClick={onComplete} data-testid={`complete-${w.id}`} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
              <DollarSign className="w-4 h-4 mr-1" /> Mark Paid
            </Button>
            <Button size="sm" variant="destructive" onClick={onReject} data-testid={`reject-${w.id}`} className="flex-1">
              <XCircle className="w-4 h-4 mr-1" /> Reject
            </Button>
          </div>
        )}
        {w.status === 'approved' && (
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={onComplete} data-testid={`complete-${w.id}`} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
              <DollarSign className="w-4 h-4 mr-1" /> Mark as Paid
            </Button>
            <Button size="sm" variant="destructive" onClick={onReject} data-testid={`reject-${w.id}`} className="flex-1">
              <XCircle className="w-4 h-4 mr-1" /> Reject & Refund
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function KYCRow({ k, onApprove, onReject }) {
  return (
    <Card data-testid={`admin-kyc-${k.user_id}`}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start gap-3 mb-3">
          <Avatar className="w-12 h-12">
            <AvatarFallback>{(k.full_name || '?').slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-bold">{k.full_name}</p>
            <p className="text-xs text-muted-foreground">ID: {k.id_number} · {k.phone}</p>
            <p className="text-xs text-muted-foreground">DOB: {k.dob || 'N/A'} · {new Date(k.submitted_at).toLocaleString()}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {k.id_document_url && (
            <a href={k.id_document_url} target="_blank" rel="noreferrer" className="block">
              <img src={k.id_document_url} alt="ID" className="w-full h-32 object-cover rounded-lg" />
              <p className="text-xs text-center mt-1 text-muted-foreground">ID Document</p>
            </a>
          )}
          {k.selfie_url && (
            <a href={k.selfie_url} target="_blank" rel="noreferrer" className="block">
              <img src={k.selfie_url} alt="Selfie" className="w-full h-32 object-cover rounded-lg" />
              <p className="text-xs text-center mt-1 text-muted-foreground">Selfie</p>
            </a>
          )}
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={onApprove} data-testid={`kyc-approve-${k.user_id}`} className="flex-1">
            <ShieldCheck className="w-4 h-4 mr-1" /> Approve
          </Button>
          <Button size="sm" variant="destructive" onClick={onReject} data-testid={`kyc-reject-${k.user_id}`} className="flex-1">
            <XCircle className="w-4 h-4 mr-1" /> Reject
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
